import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ApiError } from '@/lib/api-client';
import { useAuth } from '@/auth/use-auth';
import { Card, ErrorNote, Field, SubmitButton } from '@/components/Form';

export function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setBusy(true);

    try {
      await login(identifier, password);
      navigate('/profile');
    } catch (caught) {
      if (caught instanceof ApiError && caught.code === 'OTP_VERIFICATION_REQUIRED') {
        // The account exists but has never verified a channel. [FR-M01.1]
        navigate(`/verify?identifier=${encodeURIComponent(identifier)}`);
        return;
      }
      setError(caught instanceof Error ? caught.message : 'Could not sign in');
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={handleSubmit}>
      <Card title="Sign in" subtitle="Teens, parents and job posters">
        <ErrorNote message={error} />
        <Field
          label="Email or mobile"
          value={identifier}
          onChange={(e) => setIdentifier(e.target.value)}
          autoComplete="username"
          required
        />
        <Field
          label="Password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoComplete="current-password"
          required
        />
        <SubmitButton busy={busy}>Sign in</SubmitButton>
        <div className="flex justify-between text-sm">
          <Link to="/register" className="text-brand-600 hover:underline">
            Create an account
          </Link>
          <Link to="/forgot-password" className="text-slate-500 hover:underline">
            Forgot password?
          </Link>
        </div>
      </Card>
    </form>
  );
}
